import clsx from 'clsx';
import { KeyRound } from 'lucide-react';
import type { CredentialProvider } from '@/services/product-credentials.service';

/**
 * Simplified monochrome brand marks drawn inline (no external assets), each on
 * a tinted tile in the provider's brand colour. Deliberately geometric
 * approximations — recognisable at 16–40px, not pixel-exact logos.
 */
const TILE: Record<CredentialProvider, { bg: string; fg: string }> = {
  openai:     { bg: '#10A37F1a', fg: '#0d8a6b' },
  anthropic:  { bg: '#D977571f', fg: '#c25b3b' },
  gemini:     { bg: '#4E8CF51f', fg: '#3b73d4' },
  qwen:       { bg: '#615CED1f', fg: '#5049c9' },
  mistral:    { bg: '#FA500F1a', fg: '#e0480d' },
  grok:       { bg: '#1111111a', fg: '#1a1a1a' },
  elevenlabs: { bg: '#1111111a', fg: '#1a1a1a' },
  imagekit:   { bg: '#EE3F8C1f', fg: '#d0296f' },
  aws:        { bg: '#FF99001f', fg: '#c46f00' },
  other:      { bg: 'var(--color-primary-soft)', fg: 'var(--color-primary)' },
};

export const PROVIDER_LABEL: Record<CredentialProvider, string> = {
  openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Google Gemini', qwen: 'Qwen · DashScope',
  mistral: 'Mistral', grok: 'xAI Grok', elevenlabs: 'ElevenLabs', imagekit: 'ImageKit', aws: 'Amazon Web Services', other: 'Other',
};

function Mark({ provider, size }: { provider: CredentialProvider; size: number }) {
  const s = size, c = s / 2;
  switch (provider) {
    case 'openai':
      // Six rounded strokes around the centre — the knot silhouette.
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
          {[0, 60, 120, 180, 240, 300].map((r) => <path key={r} d="M12 4.5v5" transform={`rotate(${r} 12 12)`} />)}
          <circle cx="12" cy="12" r="3.2" />
        </svg>
      );
    case 'anthropic':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
          <path d="M13.6 5h3.1l5.3 14h-3.2l-1.1-3h-5.6l-1.1 3H7.9L13.6 5zm.8 3.6-1.9 5.1h3.8l-1.9-5.1zM2 19 7.4 5h3L5.1 19H2z" />
        </svg>
      );
    case 'gemini':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2c.6 5.5 4.5 9.4 10 10-5.5.6-9.4 4.5-10 10-.6-5.5-4.5-9.4-10-10 5.5-.6 9.4-4.5 10-10z" />
        </svg>
      );
    case 'qwen':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 4h8l4 7-4 7H8l-4-7 4-7z" />
          <path d="M12 8.5 15 11l-3 2.5L9 11l3-2.5z" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'mistral':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
          {[[3,3],[17,3],[3,8],[8,8],[12,8],[17,8],[3,13],[12,13],[17,13],[3,18],[17,18]].map(([x, y]) => <rect key={`${x}${y}`} x={x} y={y} width="4" height="4" rx="0.6" />)}
        </svg>
      );
    case 'grok':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round">
          <path d="M5 5l14 14M19 5 5 19" />
        </svg>
      );
    case 'elevenlabs':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
          <rect x="6.5" y="4" width="3.6" height="16" rx="1" /><rect x="13.9" y="4" width="3.6" height="16" rx="1" />
        </svg>
      );
    case 'imagekit':
      // Picture frame with a mountain + sun — the "image" idea in one stroke.
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
          <circle cx="9" cy="10" r="1.6" fill="currentColor" stroke="none" />
          <path d="M4 17l5-5 3.5 3.5L15 13l5 5" />
        </svg>
      );
    case 'aws':
      // The smile arc with an arrowhead.
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3.5 13.5c4.5 4.5 12.5 4.5 17 0" />
          <path d="M17.5 11.5l3 2-1.5 3.2" />
          <path d="M7 6.5l2.5 4M12 6v4.5M17 6.5l-2.5 4" strokeWidth={2} />
        </svg>
      );
    default:
      return <KeyRound size={Math.round(c)} />;
  }
}

/** Names the icon accepts — the vault's providers plus the AI-settings alias `claude` (→ anthropic tile). */
export type ProviderIconName = CredentialProvider | 'claude';
const ALIAS: Partial<Record<ProviderIconName, CredentialProvider>> = { claude: 'anthropic' };

export function ProviderIcon({ provider: raw, size = 36, className }: { provider: ProviderIconName; size?: number; className?: string }) {
  const provider: CredentialProvider = ALIAS[raw] ?? (raw as CredentialProvider);
  const t = TILE[provider] ?? TILE.other;
  return (
    <span
      className={clsx('inline-flex shrink-0 items-center justify-center rounded-xl', className)}
      style={{ width: size, height: size, background: t.bg, color: t.fg }}
      title={PROVIDER_LABEL[provider]}
      aria-label={PROVIDER_LABEL[provider]}
    >
      <Mark provider={provider} size={Math.round(size * 0.55)} />
    </span>
  );
}
